import {
  CommunicationStatus,
  InstitutionalCommunication,
  InstitutionalCommunicationFetch,
  InstitutionalCommunicationQueryParams,
} from "../../models/institutional-communications";
import { api } from "./api";

const resolvedApiBase = (process.env.REACT_APP_API || "").replace(/\/+$/, "");
const institutionalCommunicationsBasePath = resolvedApiBase.endsWith("/bo")
  ? "/institutional-communications"
  : "/bo/institutional-communications";

const resolveCommunicationStatus = (rawCommunication: any): CommunicationStatus => {
  if (rawCommunication?.status === "ACTIVE" || rawCommunication?.status === "INACTIVE") {
    return rawCommunication.status;
  }

  const isActive =
    rawCommunication?.active === true ||
    rawCommunication?.active === 1 ||
    rawCommunication?.active === "1" ||
    rawCommunication?.active === "true";

  return isActive ? "ACTIVE" : "INACTIVE";
};

const normalizeCommunication = (rawCommunication: any): InstitutionalCommunication => {
  const normalizedImageFormat =
    rawCommunication?.imageFormat || rawCommunication?.image_format || "";
  const normalizedImageBase64 =
    rawCommunication?.imageBase64 || rawCommunication?.image_base64 || "";

  const fallbackMainImageUrl =
    rawCommunication?.mainImageUrl ||
    rawCommunication?.main_image_url ||
    "";

  const parsedDataUrl =
    !normalizedImageBase64 && fallbackMainImageUrl.startsWith("data:image/")
      ? fallbackMainImageUrl
      : "";

  const parsedImageFormat = parsedDataUrl
    ? parsedDataUrl.split("image/")[1]?.split(";")[0] || ""
    : "";

  const parsedImageBase64 = parsedDataUrl
    ? parsedDataUrl.split(",")[1] || ""
    : "";

  const imageFormat = normalizedImageFormat || parsedImageFormat;
  const imageBase64 = normalizedImageBase64 || parsedImageBase64;

  // If mainImageUrl is an HTTP URL (e.g. S3), use it directly
  const isHttpUrl = fallbackMainImageUrl.startsWith("http://") || fallbackMainImageUrl.startsWith("https://");

  return {
    id: Number(rawCommunication?.id || 0),
    name: rawCommunication?.name || "",
    type:
      rawCommunication?.type ||
      rawCommunication?.eventTypeName ||
      rawCommunication?.event_type_name ||
      rawCommunication?.eventType?.eventTypeName ||
      rawCommunication?.eventType?.name ||
      "",
    description: rawCommunication?.description || "",
    imageFormat: imageFormat || (isHttpUrl ? "jpeg" : ""),
    imageBase64: imageBase64 || (isHttpUrl ? fallbackMainImageUrl : ""),
    mainImageUrl: imageBase64
      ? `data:image/${imageFormat || "jpeg"};base64,${imageBase64}`
      : fallbackMainImageUrl,
    importantMessage:
      rawCommunication?.importantMessage ?? rawCommunication?.important_message ?? null,
    additionalInformation:
      rawCommunication?.additionalInformation ??
      rawCommunication?.additional_information ??
      rawCommunication?.rules ??
      null,
    status: resolveCommunicationStatus(rawCommunication),
    actionType:
      rawCommunication?.actionType ?? rawCommunication?.action_type ?? "OPEN_DETAIL",
    redirectUrl:
      rawCommunication?.redirectUrl ?? rawCommunication?.redirect_url ?? null,
    appearanceColor:
      rawCommunication?.appearanceColor ??
      rawCommunication?.appearance_color ??
      "PRIMARY",
    eventDateTime:
      rawCommunication?.eventDateTime ||
      rawCommunication?.event_date_time ||
      rawCommunication?.startDate ||
      rawCommunication?.start_date ||
      null,
    tenantId:
      rawCommunication?.tenantId ?? rawCommunication?.tenant_id ?? null,
  };
};

const communicationTag = (id: string | number) =>
  ({ type: "institutionalCommunications" as const, id });
const communicationListTag = {
  type: "institutionalCommunications" as const,
  id: "LIST",
};

export const institutionalCommunicationsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getInstitutionalCommunications: build.query<
      InstitutionalCommunication[],
      InstitutionalCommunicationQueryParams
    >({
      query: (queryParams) => ({
        url: institutionalCommunicationsBasePath,
        params: queryParams,
      }),
      transformResponse: (response: any) => {
        const responseData = response?.success ? response?.data : response;
        const communicationList = Array.isArray(responseData)
          ? responseData
          : Array.isArray(responseData?.items)
          ? responseData.items
          : [];
        return communicationList.map(normalizeCommunication);
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => communicationTag(id)),
              communicationListTag,
            ]
          : [communicationListTag],
    }),
    getInstitutionalCommunicationById: build.query<
      InstitutionalCommunication,
      string | number
    >({
      query: (communicationId) => ({
        url: `${institutionalCommunicationsBasePath}/${communicationId}`,
      }),
      transformResponse: (response: any) => {
        const responseData = response?.success ? response?.data : response;
        const communicationData = Array.isArray(responseData)
          ? responseData[0]
          : responseData;
        return normalizeCommunication(communicationData);
      },
      providesTags: (_result, _error, communicationId) => [communicationTag(communicationId)],
    }),
    createInstitutionalCommunication: build.mutation<
      { id: number },
      InstitutionalCommunicationFetch
    >({
      query: (communicationPayload) => ({
        url: institutionalCommunicationsBasePath,
        method: "POST",
        body: communicationPayload,
      }),
      transformResponse: (response: any) => {
        if (response?.success && response?.data) {
          return response.data;
        }
        return response;
      },
      invalidatesTags: [communicationListTag],
    }),
    updateInstitutionalCommunication: build.mutation<
      void,
      InstitutionalCommunicationFetch
    >({
      query: (communicationPayload) => ({
        url: institutionalCommunicationsBasePath,
        method: "PATCH",
        body: communicationPayload,
      }),
      invalidatesTags: (_result, _error, communicationPayload) => [
        ...(communicationPayload.id != null
          ? [communicationTag(communicationPayload.id)]
          : []),
        communicationListTag,
      ],
    }),
    duplicateInstitutionalCommunication: build.mutation<void, number>({
      query: (communicationId) => ({
        url: `${institutionalCommunicationsBasePath}/${communicationId}/duplicate`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, communicationId) => [
        communicationTag(communicationId),
        communicationListTag,
      ],
    }),
    deleteInstitutionalCommunication: build.mutation<void, number>({
      query: (communicationId) => ({
        url: `${institutionalCommunicationsBasePath}/${communicationId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, communicationId) => [
        communicationTag(communicationId),
        communicationListTag,
      ],
    }),
  }),
});

export const {
  useGetInstitutionalCommunicationsQuery,
  useGetInstitutionalCommunicationByIdQuery,
  useCreateInstitutionalCommunicationMutation,
  useUpdateInstitutionalCommunicationMutation,
  useDuplicateInstitutionalCommunicationMutation,
  useDeleteInstitutionalCommunicationMutation,
} = institutionalCommunicationsApi;
